import { ITDayType } from '@app/mot/components/mot-day/mot-day.interface';

import {
  checkEqualSlotsDates,
  countDuration,
  orderSlotsInProposalByStartDate,
  SEASONS,
  SLOT_DURATION
} from './manage-vacations.constants';
import {
  ICriteriaObservations,
  IEmployeeObservations,
  ILastHolidays,
  IManageVacationsProposals,
  IMomoManageVacationsCriteria,
  IMomoManageVacationsProposals,
  IMomoSeasonsProposal,
  IMotManageVacationsCriteria,
  IProposal,
  IProposalsByOrder,
  ISeasonCalendar,
  ISeasonCalendarWeek
} from './manage-vacations.interface';

export class VacationsSeason {
  id: SEASONS;
  title: string;
  description: string;
  img!: string;

  constructor(data) {
    this.id = data.id;
    this.title = data.title;
    this.description = data.description;

    this.setImgBySeasonId();
  }

  setImgBySeasonId() {
    switch (this.id) {
      case SEASONS.summer:
        this.img = 'assets/imgs/icons/summer.svg';
        break;
      case SEASONS.winter:
        this.img = 'assets/imgs/icons/winter.svg';
        break;
    }
  }
}

export class MotVacationsCriteria {
  seasons: VacationsSeason[];
  observations: ICriteriaObservations;
  commonHolidayUsers: string;
  lastHolidays: ILastHolidays;
  alternationGuide: ILastHolidays;
  generalInformation: string;

  constructor(data: IMotManageVacationsCriteria) {
    this.seasons = data.seasons.map((season) => new VacationsSeason(season));
    this.observations = data.employee_observations;
    this.commonHolidayUsers = data.common_holiday_users_group;
    this.lastHolidays = data.last_holiday_periods;
    this.alternationGuide = data.alternation_guide;
    this.generalInformation = data.general_information;
  }
}

export class MomoVacationsCriteria {
  seasons: VacationsSeason[];
  generalInformation: string;

  constructor(data: IMomoManageVacationsCriteria) {
    this.seasons = data.seasons.map((season) => new VacationsSeason(season));
    this.generalInformation = data.general_information;
  }
}

export class VacationsProposal {
  winter: MotSeasonProposal;
  summer: MotSeasonProposal;
  observations?: IEmployeeObservations | null;
  titleMessage: string | undefined;

  constructor(data: IManageVacationsProposals) {
    this.observations = data.employee_observations || null;
    this.titleMessage = data.vacation_proposal_message;
    this.winter = new MotSeasonProposal(data.winter_proposals);
    this.summer = new MotSeasonProposal(data.summer_proposals);
  }

  userHasData() {
    return Boolean(this.winter.proposals.length || this.summer.proposals.length || this.observations);
  }

  setWinterValues() {
    this.winter.id = SEASONS.winter;
    this.winter.title = 'MANAGE_VACATIONS.WINTER_VACATIONS';
    this.winter.img = 'assets/imgs/icons/winter.svg';
  }

  setSummerValues() {
    this.summer.id = SEASONS.summer;
    this.summer.title = 'MANAGE_VACATIONS.SUMMER_VACATIONS';
    this.summer.img = 'assets/imgs/icons/summer.svg';
  }

  setMomoUser(momoUser: boolean) {
    this.winter.setMomoUser(momoUser);
    this.summer.setMomoUser(momoUser);
  }
}

export class MomoVacationsProposal {
  observations: string;
  titleMessage: string | undefined;
  preferences: MomoSeasonProposal[] = [];

  constructor(data: IMomoManageVacationsProposals) {
    this.observations = data.observations;
    this.titleMessage = data.vacation_proposal_message;
    data.preferences.forEach((preference) => this.preferences.push(new MomoSeasonProposal(preference)));
  }

  userHasData() {
    return Boolean((this.preferences && this.preferences.length) || this.observations);
  }

  proposalIsFullfilled() {
    return Boolean(this.preferences && this.preferences.length);
  }
}

export class SeasonProposal {
  id!: SEASONS;
  title!: string;
  img!: string;
  proposals: IProposal[] = [];

  constructor(data: IProposal[]) {
    this.proposals = data;
  }
}

export class MotSeasonProposal extends SeasonProposal {
  proposalsByOrder: IProposalsByOrder[] = [];
  isMomoUser: boolean = false;

  constructor(data: IProposal[]) {
    super(data);
  }

  groupProposalsByOrder() {
    this.initProposalsByOrder();
    this.proposals.forEach((proposal) => {
      const index = this.proposalsByOrder.findIndex((orderedProposal) => orderedProposal.order === proposal.order);

      this.proposalsByOrder[index].proposals.push(proposal);
    });
  }

  addSlotsToSeasonProposal(slots: IProposal[]) {
    this.proposals.push(...slots);
  }

  setValues(id: SEASONS) {
    if (id === SEASONS.summer) {
      this.id = SEASONS.summer;
      this.title = 'MANAGE_VACATIONS.SUMMER_VACATIONS';
      this.img = 'assets/imgs/icons/summer.svg';
    } else {
      this.id = SEASONS.winter;
      this.title = 'MANAGE_VACATIONS.WINTER_VACATIONS';
      this.img = 'assets/imgs/icons/winter.svg';
    }
  }

  getProposalsByOrder(order: number) {
    return this.proposals.filter((proposal) => proposal.order === order);
  }

  removeProposalsByOrder(order: number) {
    this.proposals = this.proposals.filter((proposal) => proposal.order !== order);
    this.proposalsByOrder[order - 1].proposals = [];
  }

  hasAllProposalsFullfilled() {
    return (
      this.proposalsByOrder.length === 4 &&
      this.proposalsByOrder.every((orderedProposal) => orderedProposal.proposals?.length)
    );
  }

  fullfillingLastProposal() {
    let fullfillingLast = false;

    if (this.isMomoUser) {
      fullfillingLast = Boolean(this.proposalsByOrder[0].proposals?.length);
    } else {
      for (let i = 0; i <= 2; i++) {
        fullfillingLast = Boolean(this.proposalsByOrder[i].proposals?.length);
      }
    }

    return fullfillingLast;
  }

  hasAnyProposalFullfilled() {
    return this.proposalsByOrder.some((orderedProposal) => orderedProposal.proposals?.length);
  }

  setMomoUser(momoUser: boolean) {
    this.isMomoUser = momoUser;
    this.groupProposalsByOrder();
  }

  checkProposalAlreadyAdded(slots: IProposal[]) {
    let proposalRepeated = false;
    const orderedSelectedSlots = orderSlotsInProposalByStartDate(slots);

    this.proposalsByOrder.forEach((proposalByOrder) => {
      if (!proposalRepeated) {
        const orderedProposalSlots = orderSlotsInProposalByStartDate(proposalByOrder.proposals);

        if (orderedSelectedSlots.length !== orderedProposalSlots.length) {
          return;
        }

        proposalRepeated = orderedProposalSlots.every((proposal, index) =>
          checkEqualSlotsDates(proposal, orderedSelectedSlots[index])
        );
      }
    });

    return proposalRepeated;
  }

  private initProposalsByOrder() {
    const proposalsLength = this.isMomoUser ? 2 : 4;
    this.proposalsByOrder = [];

    for (let i = 1; i <= proposalsLength; i++) {
      this.proposalsByOrder.push({
        order: i,
        proposals: []
      });
    }
  }
}

export class MomoSeasonProposal {
  winter: SeasonProposal;
  summer: SeasonProposal;
  order?: number;
  currentWeight: number = 0;

  readonly maxWeight = 4;

  constructor(data: IMomoSeasonsProposal) {
    this.winter = {
      id: SEASONS.winter,
      title: 'MANAGE_VACATIONS.WINTER_VACATIONS',
      img: 'assets/imgs/icons/winter.svg',
      proposals: data.winter
    };
    this.summer = {
      id: SEASONS.summer,
      title: 'MANAGE_VACATIONS.SUMMER_VACATIONS',
      img: 'assets/imgs/icons/summer.svg',
      proposals: data.summer
    };
  }

  addSummerProposals(slots: IProposal[]) {
    this.summer.proposals = slots;
  }

  addWinterProposals(slots: IProposal[]) {
    this.winter.proposals = slots;
  }

  setProposalOrder(order: number) {
    this.order = order;
  }

  updateWeight(weight: number) {
    this.currentWeight += weight;
  }

  setInitialWeight() {
    this.currentWeight = countDuration(this.summer.proposals) + countDuration(this.winter.proposals);
  }

  checkSlotDurations(slots: IProposal[]) {
    return this.summer.proposals[0].duration === SLOT_DURATION.summer_short && slots.length < 2;
  }

  checkDuplicatedProposals(preferences: MomoSeasonProposal[]) {
    let proposalRepeated = true;

    // Separate proposals slots by summer/winter
    const summerPreferenceProposals = preferences.map((preference) => preference.summer.proposals);
    const winterPreferenceProposals = preferences.map((preference) => preference.winter.proposals);

    // Order proposals slots inside summer/winter arrays
    const orderedSummerPreferenceProposals = summerPreferenceProposals.map((preference) =>
      orderSlotsInProposalByStartDate(preference)
    );
    const orderedWinterPreferenceProposals = winterPreferenceProposals.map((preference) =>
      orderSlotsInProposalByStartDate(preference)
    );

    if (preferences.length < 2) {
      proposalRepeated = false;
    }

    // Iterate over preferences comparing index element with the next one in the array
    // First time we find a difference between arrays length or objects values, break loops and return
    for (let i = 0; i <= preferences.length - 2; i++) {
      // Check arrays length
      if (orderedSummerPreferenceProposals[i].length === orderedSummerPreferenceProposals[i + 1].length) {
        // eslint-disable-next-line
        orderedSummerPreferenceProposals[i].forEach((orderedSummerPreferenceProposal, index) => {
          // Check object values
          if (!checkEqualSlotsDates(orderedSummerPreferenceProposal, orderedSummerPreferenceProposals[i + 1][index])) {
            proposalRepeated = false;
          }
        });
      } else {
        proposalRepeated = false;
        break;
      }

      // Same logic for winter preferences
      if (orderedWinterPreferenceProposals[i].length === orderedWinterPreferenceProposals[i + 1].length) {
        // eslint-disable-next-line
        orderedWinterPreferenceProposals[i].forEach((orderedWinterPreferenceProposal, index) => {
          if (!checkEqualSlotsDates(orderedWinterPreferenceProposal, orderedWinterPreferenceProposals[i + 1][index])) {
            proposalRepeated = false;
          }
        });
      } else {
        proposalRepeated = false;
        break;
      }
    }

    return proposalRepeated;
  }
}

export class SeasonCalendar {
  months: {
    yearLabel: string;
    monthLabel: string;
    slots: IProposal[];
    weeks: ISeasonCalendarWeek[];
  }[];
  colours: { primaryColour: string; secondaryColour: string };
  captions: ITDayType[];
  maxHolidays: number;
  minHolidays: number;

  constructor(data: ISeasonCalendar) {
    this.months = data.calendar;
    this.colours = data.colours;
    this.captions = data.captions;
    this.maxHolidays = data.max_holidays;
    this.minHolidays = data.min_holidays;
  }
}
